import React, { useState } from 'react';
import { Condition, ConditionGroup, Document, ConditionStatus } from './types';
import { StatusSummaryCards } from './components/StatusSummaryCards';
import { ConditionTable } from './components/ConditionTable';
import { AddConditionForm } from './components/AddConditionForm';
import { DocumentAssignmentModal } from './components/DocumentAssignmentModal';
// Seed data
const initialConditions: Condition[] = [{
  id: 'cond-1',
  name: 'Settlement Certification',
  description: 'Settlement Agent and Seller needs to sign Settlement Certification.',
  status: 'new',
  createdAt: new Date('2024-12-09'),
  assignedTo: 'Rama Kacho',
  groupId: 'group-1'
}, {
  id: 'cond-2',
  name: 'Proof of Homeowners Insurance',
  description: 'Borrower must provide current homeowners insurance declaration page.',
  status: 'requested',
  createdAt: new Date('2024-12-05'),
  requestedAt: new Date('2024-12-06'),
  assignedTo: 'Rama Kacho',
  groupId: 'group-1'
}, {
  id: 'cond-3',
  name: 'Final Inspection Report',
  description: 'Property inspection must be completed and report submitted.',
  status: 'review',
  createdAt: new Date('2024-12-01'),
  requestedAt: new Date('2024-12-02'),
  assignedTo: 'Sarah Chen',
  groupId: undefined
}, {
  id: 'cond-4',
  name: 'Title Search Results',
  description: 'Title company to provide clear title search results.',
  status: 'new',
  createdAt: new Date('2024-12-10'),
  assignedTo: 'Mike Torres',
  groupId: 'group-2'
}, {
  id: 'cond-5',
  name: 'Flood Zone Determination',
  description: 'FEMA flood zone certification required.',
  status: 'cleared',
  createdAt: new Date('2024-11-28'),
  assignedTo: 'Sarah Chen',
  groupId: undefined,
  satisfiedByDocumentId: 'doc-1'
}, {
  id: 'cond-6',
  name: 'VOE - Verbal Verification of Employment',
  description: 'Verbal verification of employment within 10 days of closing.',
  status: 'new',
  createdAt: new Date('2024-12-11'),
  assignedTo: 'Rama Kacho',
  groupId: 'group-2'
}, {
  id: 'cond-7',
  name: 'Updated Credit Report',
  description: 'Pull updated credit report — previous one expired.',
  status: 'requested',
  createdAt: new Date('2024-12-03'),
  requestedAt: new Date('2024-12-04'),
  assignedTo: 'Mike Torres',
  groupId: undefined
}];
const initialGroups: ConditionGroup[] = [{
  id: 'group-1',
  name: 'Prior to Closing',
  note: 'Must be completed before scheduled closing date of Jan 15',
  isExpanded: true
}, {
  id: 'group-2',
  name: 'Title & Compliance',
  note: '',
  isExpanded: true
}];
const initialDocuments: Document[] = [{
  id: 'doc-1',
  name: 'FEMA Flood Certificate.pdf',
  conditionIds: ['cond-5'],
  createdAt: new Date('2024-12-01')
}];
export function App() {
  const [conditions, setConditions] = useState<Condition[]>(initialConditions);
  const [groups, setGroups] = useState<ConditionGroup[]>(initialGroups);
  const [documents, setDocuments] = useState<Document[]>(initialDocuments);
  const [isAddingCondition, setIsAddingCondition] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  // Derived state
  const counts = {
    new: conditions.filter(c => c.status === 'new').length,
    requested: conditions.filter(c => c.status === 'requested').length,
    review: conditions.filter(c => c.status === 'review').length,
    cleared: conditions.filter(c => c.status === 'cleared').length
  };
  const activeConditions = conditions.filter(c => c.status !== 'cleared');
  const clearedConditions = conditions.filter(c => c.status === 'cleared');
  // Condition Actions
  const addCondition = (data: {
    name: string;
    description: string;
    assignedTo: string;
    groupId?: string;
  }) => {
    const newCond: Condition = {
      id: crypto.randomUUID(),
      name: data.name,
      description: data.description,
      assignedTo: data.assignedTo,
      groupId: data.groupId,
      status: 'new',
      createdAt: new Date()
    };
    setConditions(prev => [newCond, ...prev]);
    setIsAddingCondition(false);
  };
  const removeCondition = (id: string) => {
    setConditions(prev => prev.filter(c => c.id !== id));
  };
  const updateConditionStatus = (id: string, status: ConditionStatus) => {
    setConditions(prev => prev.map(c => {
      if (c.id !== id) return c;
      const updates: Partial<Condition> = {
        status
      };
      if (status === 'requested' && !c.requestedAt) updates.requestedAt = new Date();
      return {
        ...c,
        ...updates
      };
    }));
  };
  const handleGroupByNote = (conditionIds: string[], note: string) => {
    const newGroupId = crypto.randomUUID();
    const newGroup: ConditionGroup = {
      id: newGroupId,
      name: note,
      note: note,
      isExpanded: true
    };
    setGroups(prev => [...prev, newGroup]);
    setConditions(prev => prev.map(c => conditionIds.includes(c.id) ? {
      ...c,
      groupId: newGroupId
    } : c));
  };
  // Document Actions
  const addDocument = (documentName: string, conditionIds: string[]) => {
    const newDoc: Document = {
      id: crypto.randomUUID(),
      name: documentName,
      conditionIds,
      createdAt: new Date()
    };
    setDocuments(prev => [...prev, newDoc]);
    // Mark selected conditions as cleared
    setConditions(prev => prev.map(c => conditionIds.includes(c.id) ? {
      ...c,
      status: 'cleared',
      satisfiedByDocumentId: newDoc.id
    } : c));
  };
  return <div className="min-h-screen w-full bg-[#f4f6f9] text-slate-800 font-sans" data-id="element-0">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm" data-id="element-1">
        <div className="max-w-7xl mx-auto px-4 py-3" data-id="element-2">
          <div className="flex items-center justify-between" data-id="element-3">
            <div data-id="element-4" />
            <div className="flex items-center gap-3" data-id="element-5">
              <button onClick={() => setIsAddingCondition(true)} className="px-4 py-1.5 text-sm font-medium text-[#005fb2] border border-[#005fb2] rounded-full hover:bg-blue-50 transition-colors" data-id="element-6">
                Add Condition(s)
              </button>
              <button onClick={() => setIsDocumentModalOpen(true)} className="px-4 py-1.5 text-sm font-medium bg-[#e3243b] text-white rounded-full hover:bg-red-700 transition-colors shadow-sm" data-id="element-7">
                Upload Document
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6" data-id="element-8">
        <StatusSummaryCards counts={counts} data-id="element-9" />

        <div className="flex items-center justify-between mb-4" data-id="element-10">
          <h2 className="text-xl font-light text-[#005fb2]" data-id="element-11">
            Outstanding Conditions ({activeConditions.length})
          </h2>
        </div>

        {isAddingCondition && <AddConditionForm groups={groups} onAdd={addCondition} onCancel={() => setIsAddingCondition(false)} data-id="element-12" />}

        <div className="space-y-8" data-id="element-13">
          <div className="shadow-sm rounded-lg" data-id="element-14">
            <ConditionTable conditions={activeConditions} groups={groups} documents={documents} onStatusChange={updateConditionStatus} onRemove={removeCondition} onGroupByNote={handleGroupByNote} data-id="element-15" />
          </div>

          {clearedConditions.length > 0 && <div data-id="element-16">
              <h3 className="text-lg font-light text-emerald-700 mb-3" data-id="element-17">
                Cleared Conditions ({clearedConditions.length})
              </h3>
              <div className="shadow-sm rounded-lg opacity-80 hover:opacity-100 transition-opacity" data-id="element-18">
                <ConditionTable conditions={clearedConditions} groups={groups} documents={documents} onStatusChange={updateConditionStatus} onRemove={removeCondition} onGroupByNote={handleGroupByNote} data-id="element-19" />
              </div>
            </div>}
        </div>
      </main>

      <DocumentAssignmentModal isOpen={isDocumentModalOpen} conditions={conditions} groups={groups} onClose={() => setIsDocumentModalOpen(false)} onSubmit={addDocument} data-id="element-20" />
    </div>;
}